import { useEffect } from "react";
import { getStoredAccessTokenExpiryMs } from "../utils/sessionExpiry";
import { logoutFun } from "../component/HomeSideNavBar";

// Cap how far out we'll schedule a raw setTimeout — an inactive background tab can have its
// timers throttled/coalesced by the browser for a very long-lived delay, and some environments
// balk at a huge millisecond value. Re-checking well before a token could ever live this long is
// harmless and keeps the schedule accurate even after a long throttle.
const MAX_TIMER_MS = 30 * 60 * 1000;

/**
 * Proactively logs the user out the instant their access token's own `exp` claim is reached —
 * even if they never trigger another API call. Without this, an idle/backgrounded tab (or one
 * where the next click doesn't happen to fire a network request) keeps rendering whatever was
 * last fetched until *something* eventually gets a 401 from the server; reported as "dashboard
 * shows data, but you have to try something (or manually refresh) to actually get logged out".
 *
 * Reuses the app's normal `logoutFun` (clears storage, redirects to "/") so an expired-token
 * logout looks identical to any other logout, on every affected route via `PrivateRoute`. Scoped
 * to non-Track-Point users — the mobile Track Point flow doesn't touch the web client at all.
 *
 * `enabled` should be the same "is there a session at all" condition the caller already gates
 * rendering on (e.g. `PrivateRoute`'s `isAuthenticated` check) so this never runs on the public
 * landing/login page.
 */
export function useSessionExpiryGuard(enabled) {
  useEffect(() => {
    if (!enabled) return undefined;

    let timer = null;

    const scheduleCheck = () => {
      const expiryMs = getStoredAccessTokenExpiryMs();
      if (expiryMs === null) return; // unparseable/missing — let a real 401 decide

      const remaining = expiryMs - Date.now();
      if (remaining <= 0) {
        logoutFun();
        return;
      }
      timer = setTimeout(scheduleCheck, Math.min(remaining, MAX_TIMER_MS));
    };

    scheduleCheck();
    return () => {
      if (timer) clearTimeout(timer);
    };
  }, [enabled]);
}
